import {
  Component,
  Input,
  ElementRef,
  ViewChild,
  OnInit,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  TemplateRef,
  ViewContainerRef,
  Renderer2,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { InlineFieldInterfaceComponent } from '../inline-field-interface.component';
import { PartialFormField } from '../../form-fields/form-field.types';
import { Account, Activity } from 'app/core/models/account.types';
import { AccountService } from 'app/core';
import { filter } from 'rxjs';
import { ConfirmService } from '../../widgets/dialog/confirm/confirm.service';

@Component({
  templateUrl: './money-inline-field.component.html',
  styleUrls: ['./money-inline-field.component.scss'],
})
export class MoneyInlineFieldComponent implements InlineFieldInterfaceComponent, OnInit {
  @ViewChild('inputControl') inputControl: ElementRef;
  @Input() model: any;
  @Input() fieldDef: any;
  @Input() data: any;
  @Input() excludeLabels: boolean = false;
  @Input() readonly: boolean = false;
  @Input() canClick: boolean = false;
  @Output() onClick: EventEmitter<PartialFormField> = new EventEmitter();
  @Output() update: EventEmitter<PartialFormField> = new EventEmitter();

  public editing: boolean = false;
  public value: any;
  public showHistory: boolean = false;
  public activities: Activity[];
  public showTip: string;
  private _originalValue;
  private _readonly = false;
  private _historyPanelOverlayRef: OverlayRef;
  precision: number = 0;

  @ViewChild('historyPanel')
  private _historyPanel: TemplateRef<any>;

  @ViewChild('historyPanelOrigin')
  private _historyPanelOrigin: ElementRef;

  constructor(
    private _accountService: AccountService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _overlay: Overlay,
    private _viewContainerRef: ViewContainerRef,
    private _renderer2: Renderer2,
    private confirmService: ConfirmService,
  ) {}

  ngOnInit() {
    this.value = this.fieldDef.meta?.value_hidden ? 'REDACTED' : this.model[this.fieldDef.name];

    // we want to track the original value, so that we don't hit save to many times
    this._originalValue = this.value;
    this.showTip = this.fieldDef.meta?.calc_code;
    if (this.fieldDef.meta && this.fieldDef.meta.readonly) {
      this._readonly = this.fieldDef.meta.readonly;
    }
    if (this.readonly) {
      this._readonly = this.readonly;
    }
    this.readonly = this._readonly;

    if (this.fieldDef.meta && this.fieldDef.meta.scale !== null && this.fieldDef.meta.scale !== undefined) {
      this.precision = this.fieldDef.meta.scale;
    }
  }

  /**
   * When the model is updated after save, re-run our logic
   * @param model
   */
  updateChangedModel(model: any): void {
    this.model = model;
    //this.value = this.model[this.fieldDef.name] * 100;
    this.value = this.model[this.fieldDef.name];

    // we want to track the original value, so that we don't hit save to many times
    this._originalValue = this.value;
  }

  edit() {
    if (!this.editing && !this._readonly) {
      this.editing = true;
      setTimeout(() => {
        this.inputControl.nativeElement.focus();
      }, 300);
    }
  }

  displayPrecision() {
    return '1.' + this.precision + '-' + this.precision;
  }

  onBlur($event: Event) {
    this.onSave();
    this.editing = false;
  }

  onSave() {
    if (this._originalValue != this.value) {
      this.updateModel();
      this.update.emit({ [this.fieldDef.name]: this.value });
    }
  }

  clickField($event) {
    this.onClick.emit($event);
  }

  updateModel() {
    this.model[this.fieldDef.name] = this.value;
    this._originalValue = this.value;
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    //RRS TODO: adding _cents since Trail records _cents field
    this._accountService
      .getActivities({ project_id: this.model.id, field: this.fieldDef.name })
      .subscribe((activities) => {
        this.activities = activities;
        this._changeDetectorRef.markForCheck();
      });
  }

  deleteActivity(activity: Activity): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        this._accountService.deleteActivity(activity.id).subscribe(() => {
          this._accountService
            .getActivities({ project_id: this.model.id, field: this.fieldDef.name })
            .subscribe((activities) => {
              this.activities = activities;
              this._changeDetectorRef.markForCheck();
            });
        });
      });
  }

  openHistoryPanel(): void {
    // Create the overlay
    this._historyPanelOverlayRef = this._overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._historyPanelOrigin.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this._historyPanelOverlayRef.attachments().subscribe(() => {
      // Add a class to the origin
      this._renderer2.addClass(this._historyPanelOrigin.nativeElement, 'panel-opened');

      // Focus to the search input once the overlay has been attached
      //this._historyPanelOverlayRef.overlayElement.querySelector('input').focus();
      this._accountService
        .getActivities({ project_id: this.model.id, field: this.fieldDef.name })
        .subscribe((activities) => {
          this.activities = activities;
          this._changeDetectorRef.markForCheck();
        });
    });

    // Create a portal from the template
    const templatePortal = new TemplatePortal(this._historyPanel, this._viewContainerRef);

    // Attach the portal to the overlay
    this._historyPanelOverlayRef.attach(templatePortal);

    // Subscribe to the backdrop click
    this._historyPanelOverlayRef.backdropClick().subscribe(() => {
      // Remove the class from the origin
      this._renderer2.removeClass(this._historyPanelOrigin.nativeElement, 'panel-opened');

      // If overlay exists and attached...
      if (this._historyPanelOverlayRef && this._historyPanelOverlayRef.hasAttached()) {
        // Detach it
        this._historyPanelOverlayRef.detach();
      }

      // If template portal exists and attached...
      if (templatePortal && templatePortal.isAttached) {
        // Detach it
        templatePortal.detach();
      }
    });
  }
}
